import React, { useState } from "react";
import { Content as AntContent } from "antd/es/layout/layout";
import {
  GoogleMap,
  PolygonF,
  DrawingManager,
  Marker,
  useJsApiLoader,
  Circle,
  Autocomplete,
} from "@react-google-maps/api";

import styled from "styled-components";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getBranchInfo, setDeliveryZones } from "../../../service";
import { useParams } from "react-router-dom";
import { Button, Flex, Form, Input, Modal, Result, Radio, InputNumber, Checkbox } from "antd";
import { colors } from "../../../styles/theme";
import { useThemeContext } from "../../../contexts/theme-context/useThemeContext";
import { useTranslation } from "react-i18next";

export const Content = styled(AntContent)`
  padding: 8px;
  min-height: 100%;
  max-height: 100%;
  height: 100%;
  margin: 0;
`;
const libraries: ("places" | "geometry" | "drawing")[] = [
  "geometry",
  "places",
  "drawing",
];

export const DeliveryMap: React.FC<any> = ({ zones, branchId: propBranchId }) => {
  const { t } = useTranslation();
  const { theme } = useThemeContext();
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyA3lh6-T9qUYkdo46jFI1jaNQ_z2JNdCEs",
    libraries,
  });

  const [form] = Form.useForm();
  const { id: paramId } = useParams();
  const id = propBranchId || paramId;
  const [startPoint, setStartPoint] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paths, setPaths] = useState<any>(null);
  const [zoneType, setZoneType] = useState<"polygon" | "radius">("polygon");
  const [radiusKm, setRadiusKm] = useState<number>(5);
  const [enableFreeDelivery, setEnableFreeDelivery] = useState(false);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const query = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(setDeliveryZones, {
    onSuccess: async (data) => {
      setPaths(null);
      setIsModalOpen(false);
      await query.invalidateQueries(["/delivery-zones"]);
      await query.refetchQueries(["/delivery-zones"]);
    },
  });

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const price = parseFloat(values.price) * 100;
        const freeAbove = enableFreeDelivery ? parseFloat(values.freeAbove) * 100 : null;
        const minimumOrderValue = parseFloat(values.minimumOrderValue || 0) * 100;

        if (zoneType === "radius") {
          // Radius-based zone - use branch location (startPoint) as center
          if (startPoint && id && price !== undefined) {
            mutateAsync({
              data: {
                name: values.name,
                price,
                freeAbove,
                minimumOrderValue,
                radiusKm: radiusKm,
                centerLat: startPoint.lat,
                centerLng: startPoint.lng,
                companyBranchId: parseInt(id),
              },
            });
            form.resetFields();
            setEnableFreeDelivery(false);
          }
        } else {
          // Polygon zone
          if (paths && id && price !== undefined) {
            const zones = paths
              .getPath()
              .getArray()
              .map((point: any, index: number) => ({
                index: index,
                lat: point.lat(),
                lng: point.lng(),
              }));

            mutateAsync({
              data: {
                name: values.name,
                price,
                freeAbove,
                minimumOrderValue,
                companyBranchId: parseInt(id),
                zones: zones,
              },
            });
            form.resetFields();
            setEnableFreeDelivery(false);
          }
        }
      })
      .catch((errorInfo) => {
      });
  };

  const handleCancel = () => {
    if (paths) {
      paths.setMap(null);
      setPaths(null);
    }
    setIsModalOpen(false);
    form.resetFields();
    setEnableFreeDelivery(false);
  };

  const [configError, setConfigError] = useState<any>(false);

  const { isLoading: isLoadingBranchInformation } = useQuery<any, Error>(
    ["/branch-information", id],
    () => getBranchInfo({ id }),
    {
      onSuccess(data) {
        setConfigError(false);
        if (data?.adress) {
          setStartPoint({
            lat: data.adress.lat,
            lng: data.adress.lng,
          });
        } else {
          setConfigError(true);
        }
      },
      enabled: !!id,
    }
  );

  const mapContainerStyle = {
    width: "100%",
    height: "calc(100% - 45px)",
  };

  const [draw, setDraw] = useState<any>(false);
  const [address, setAddress] = useState("");
  const [isAddressInsideArea, setIsAddressInsideArea] = useState<any>("");
  const [map, setMap] = useState<any>(null);
  const autocompleteRef = React.useRef<google.maps.places.Autocomplete | null>(null);

  const onLoad = React.useCallback(
    function callback(map: google.maps.Map) {
      setMap(map);
    },
    [startPoint]
  );

  const handlePolygonComplete = (polygon: google.maps.Polygon) => {
    setPaths(polygon);
    showModal();
  };

  const onAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location) {
        const addressLatLng = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        let isInside = false;
        for (const zone of zones || []) {
          if (zone.radiusKm && zone.centerLat && zone.centerLng) {
            const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
              new window.google.maps.LatLng(addressLatLng),
              new window.google.maps.LatLng(zone.centerLat, zone.centerLng)
            );
            if (distance <= zone.radiusKm * 1000) {
              isInside = true;
              break;
            }
          } else if (zone.zones && zone.zones.length > 0) {
            const path = [...zone.zones]
              .sort((a: any, b: any) => a.index - b.index)
              .map((e: any) => ({ lat: e.lat, lng: e.lng }));

            if (
              window.google.maps.geometry.poly.containsLocation(
                new window.google.maps.LatLng(addressLatLng),
                new window.google.maps.Polygon({ paths: path })
              )
            ) {
              isInside = true;
              break;
            }
          }
        }

        setIsAddressInsideArea(isInside as any);
        setAddress(place.formatted_address || "");
      }
    }
  };

  if (isLoadingBranchInformation) return null;
  if (configError)
    return (
      <Flex
        justify="center"
        align="center"
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <Result
          status="warning"
          title={t("deliveryMap.addressConfigWarning")}
        />
      </Flex>
    );

  if (isLoaded && !isLoadingBranchInformation && !configError)
    return (
      <>
        <Content>
          <Flex
            justify="start"
            gap={8}
            align="center"
            style={{
              marginBottom: 10,
            }}
          >
            <Radio.Group
              value={zoneType}
              onChange={(e) => {
                setZoneType(e.target.value);
                setDraw(false);
                if (paths) {
                  paths.setMap(null);
                  setPaths(null);
                }
              }}
            >
              <Radio.Button value="polygon">{t("deliveryMap.drawZone")}</Radio.Button>
              <Radio.Button value="radius">{t("deliveryMap.radius")}</Radio.Button>
            </Radio.Group>

            {zoneType === "radius" && (
              <>
                <InputNumber
                  min={0.1}
                  max={50}
                  step={0.1}
                  value={radiusKm}
                  onChange={(value) => setRadiusKm(value || 5)}
                  addonAfter="km"
                  style={{ width: 120 }}
                  parser={(value) => {
                    if (typeof value === 'string') {
                      const replaced = value.replace(',', '.');
                      return parseFloat(replaced) || 0;
                    }
                    return parseFloat(value || '0');
                  }}
                />
                <Button
                  type="primary"
                  onClick={showModal}
                >
                  {t("deliveryMap.createZone")}
                </Button>
              </>
            )}

            {zoneType === "polygon" && (
              <Button onClick={() => setDraw(!draw)} type={draw ? "default" : "primary"}>
                {draw ? t("deliveryMap.finishDrawing") : t("deliveryMap.startDrawing")}
              </Button>
            )}

            <Autocomplete
              onLoad={onAutocompleteLoad}
              onPlaceChanged={onPlaceChanged}
            >
              <Input
                type="text"
                style={{
                  maxWidth: 300,
                }}
                placeholder={t("deliveryMap.enterTestAddress")}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </Autocomplete>

            {isAddressInsideArea === true && (
              <p>{t("deliveryMap.addressInsideZone")}</p>
            )}
            {isAddressInsideArea === false && (
              <p>{t("deliveryMap.addressOutsideZones")}</p>
            )}
            {isAddressInsideArea === null && <p>{t("deliveryMap.invalidAddress")}</p>}
          </Flex>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={startPoint}
            zoom={11}
            onLoad={onLoad}
          >
            {zones?.length > 0 &&
              zones.map((zone: any, index: number) => {
                if (zone.radiusKm && zone.centerLat && zone.centerLng) {
                  return (
                    <Circle
                      key={zone.id || index}
                      center={{ lat: zone.centerLat, lng: zone.centerLng }}
                      radius={zone.radiusKm * 1000}
                      options={{
                        strokeColor: theme.colors.error,
                        strokeOpacity: 0.4,
                        strokeWeight: 5,
                        fillColor: "transparent",
                        fillOpacity: 0,
                        zIndex: 1,
                      }}
                    />
                  );
                }

                if (zone.zones && zone.zones.length > 0) {
                  const coords = [...zone.zones]
                    .sort((a, b) => a.index - b.index)
                    .map((e: any) => ({ lat: e.lat, lng: e.lng }));

                  return (
                    <PolygonF
                      key={zone.id || index}
                      path={coords}
                      options={{
                        strokeColor: theme.colors.error,
                        strokeOpacity: 0.4,
                        strokeWeight: 5,
                        fillColor: "transparent",
                        fillOpacity: 0,
                      }}
                    />
                  );
                }
                return null;
              })}
            {draw && zoneType === "polygon" && (
              <DrawingManager
                onPolygonComplete={handlePolygonComplete}
                options={{
                  drawingControl: false,
                  drawingMode: "polygon" as any,
                }}
              />
            )}
            {zoneType === "radius" && startPoint && (
              <Circle
                center={startPoint}
                radius={radiusKm * 1000} // Convert km to meters
                options={{
                  strokeColor: theme.colors.error,
                  strokeOpacity: 0.5,
                  strokeWeight: 5,
                  fillColor: theme.colors.error,
                  fillOpacity: 0.05,
                }}
              />
            )}
            {startPoint && <Marker position={startPoint} />}
          </GoogleMap>
        </Content>
        <Modal
          title={t("deliveryMap.addDeliveryZoneTitle")}
          open={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          okButtonProps={{
            loading: isLoading,
            disabled: isLoading,
          }}
          cancelButtonProps={{
            disabled: isLoading,
          }}
        >
          <Form layout="vertical" form={form}>
            <Form.Item
              label={t("deliveryMap.zoneNameLabel")}
              name="name"
              rules={[{ required: true, message: t("deliveryMap.fieldRequired") }]}
            >
              <Input
                style={{
                  paddingTop: "8px",
                  paddingBottom: "8px",
                }}
                pattern="^\d+(\.\d{1,2})?$"
              />
            </Form.Item>
            <Form.Item
              label={t("deliveryMap.zonePriceLabel")}
              name="price"
              rules={[{ required: true, message: t("deliveryMap.fieldRequired") }]}
            >
              <Input
                type="number"
                style={{
                  paddingTop: "8px",
                  paddingBottom: "8px",
                }}
                pattern="^\d+(\.\d{1,2})?$"
              />
            </Form.Item>

            <Form.Item
              label={t("deliveryMap.minimumOrderValueLabel")}
              name="minimumOrderValue"
              initialValue={0}
              rules={[{ required: true, message: t("deliveryMap.fieldRequired") }]}
            >
              <Input
                type="number"
                style={{
                  paddingTop: "8px",
                  paddingBottom: "8px",
                }}
                pattern="^\d+(\.\d{1,2})?$"
              />
            </Form.Item>

            <Form.Item
              label={
                <Checkbox
                  checked={enableFreeDelivery}
                  onChange={(e) => {
                    setEnableFreeDelivery(e.target.checked);
                    if (!e.target.checked) {
                      form.setFieldsValue({ freeAbove: undefined });
                    }
                  }}
                >
                  {t("deliveryMap.freeDeliveryAboveLabel")}
                </Checkbox>
              }
              name="freeAbove"
              rules={[{ required: enableFreeDelivery, message: t("deliveryMap.fieldRequired") }]}
            >
              <Input
                type="number"
                disabled={!enableFreeDelivery}
                style={{
                  paddingTop: "8px",
                  paddingBottom: "8px",
                }}
                pattern="^\d+(\.\d{1,2})?$"
              />
            </Form.Item>
          </Form>
        </Modal>
      </>
    );
  return null;
};
